<template>
  <div :dir="isHebrew ? 'rtl' : 'ltr'">
    <SingleRideHeader
      :presentedText="t('editProfilePageTitle')"
    />
    <div class="trip-card-section">
      <div class="container">
        <div class="trip-card-top trip-card-top2">
          <a
            class="trip-btn-bg btn-greey"
            @click="goBack">
            {{ t('editProfileBackToMyTrips') }}
          </a>
          <h4>{{ t('editProfileDetails') }}</h4>
        </div>
      </div>
    </div>
    <!-- End card section -->

    <div class="trip-contact-form-section">
      <div class="container">
        <div class="trip-contact-wrap">
          <h4>{{ t('editProfileDetails') }}</h4>
          <div class="row" :class="{ 'row-ltr': !isHebrew }">
            <div class="col-lg-5 offset-lg-3">
              <div class="trip-contact-column">
                <div v-if="isLoading" class="text-center py-5">
                  <div class="spinner-border text-primary" role="status">
                    <span class="sr-only">{{ t('editProfileLoading') }}</span>
                  </div>
                  <p class="mt-3">{{ t('editProfileLoadingData') }}</p>
                </div>

                <template v-else>
                  <!-- Profile Image -->
                  <div class="trip-contact-field profile-image-section">
                    <label>{{ t('editProfileImageLabel') }}</label>
                    <p class="profile-image-hint">
                      {{ t('editProfileImageHint') }}
                    </p>
                    <div class="profile-image-wrapper">
                      <div v-if="profileData.profile_image" class="profile-image-preview">
                        <img :src="profileData.profile_image" alt="Profile" class="profile-preview-img">
                      </div>
                      <div v-else class="profile-image-placeholder">
                        <i class="fas fa-user fa-3x"></i>
                        <p>{{ t('editProfileNoImage') }}</p>
                      </div>
                      <div class="trip-contact-input file-uploade">
                        <span class="btn_upload">
                          <input
                            type="file"
                            name="file"
                            accept="image/*"
                            class="input-img"
                            @change="uploadImage"
                            id="file">
                          <span v-if="!isUploading">{{ profileData.profile_image ? t('editProfileChangeImage') : t('editProfileUploadImage') }}</span>
                          <span v-else class="upload-progress">{{ t('editProfileUploading') }} {{ Math.round(uploadValue) }}%</span>
                        </span>
                        <div v-if="isUploading" class="upload-progress-bar">
                          <div class="upload-progress-fill" :style="{ width: uploadValue + '%' }"></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Name -->
                  <div class="trip-contact-field">
                    <label>{{ t('editProfileNameLabel') }}<span>*</span></label>
                    <div class="trip-contact-input">
                      <input
                        v-model="localName"
                        :placeholder="t('editProfileNamePlaceholder')"
                        type="text"
                        required
                      >
                    </div>
                  </div>

                  <!-- Title -->
                  <div class="trip-contact-field">
                    <label>{{ t('editProfileTitleLabel') }}</label>
                    <div class="trip-contact-input">
                      <input
                        v-model="localTitle"
                        :placeholder="t('editProfileTitlePlaceholder')"
                        type="text"
                      >
                    </div>
                  </div>

                  <!-- About (Free Text) -->
                  <div class="trip-contact-field">
                    <label>{{ t('editProfileAboutLabel') }}</label>
                    <div class="trip-contact-input">
                      <textarea
                        v-model="localAbout"
                        :placeholder="t('editProfileAboutPlaceholder')"
                      >
                      </textarea>
                    </div>
                  </div>

                  <!-- Website Link -->
                  <div class="trip-contact-field">
                    <label>{{ t('editProfileWebsiteLabel') }}</label>
                    <div class="trip-contact-input">
                      <input
                        v-model="localWebsiteLink"
                        placeholder="https://example.com"
                        type="url"
                        style="direction: ltr;"
                      >
                    </div>
                  </div>

                  <!-- Facebook Link -->
                  <div class="trip-contact-field">
                    <label>{{ t('editProfileFacebookLabel') }}</label>
                    <div class="trip-contact-input">
                      <input
                        v-model="localFacebookLink"
                        placeholder="https://facebook.com/yourpage"
                        type="url"
                        style="direction: ltr;"
                      >
                    </div>
                  </div>

                  <!-- Instagram Link -->
                  <div class="trip-contact-field">
                    <label>{{ t('editProfileInstagramLabel') }}</label>
                    <div class="trip-contact-input">
                      <input
                        v-model="localInstagramLink"
                        placeholder="https://instagram.com/yourprofile"
                        type="url"
                        style="direction: ltr;"
                      >
                    </div>
                  </div>

                  <!-- Action Buttons -->
                  <div class="action-buttons-wrapper">
                    <div class="trip-submit-button">
                      <a class="trip-default-btn btn-sky" @click="saveProfile" :class="{ 'disabled': isSaving }">
                        <span v-if="!isSaving">
                          <i class="fas fa-save" :style="isHebrew ? 'margin-left: 8px;' : 'margin-right: 8px;'"></i>
                          {{ t('editProfileSave') }}
                        </span>
                        <span v-else>
                          <div class="btn-spinner"></div>
                          {{ t('editProfileSaving') }}
                        </span>
                      </a>
                    </div>
                    <div class="back-button-wrapper">
                      <a class="trip-default-btn btn-sky btn-greey" @click="goBack">
                        {{ t('editProfileBackToMyTrips') }}
                        <i class="fas fa-arrow-right" :style="isHebrew ? 'margin-left: 8px;' : 'margin-right: 8px;'"></i>
                      </a>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <PageFooter />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import PageFooter from "@/components/PageFooter.vue";
import firebase from 'firebase/app';
import 'firebase/storage';
import SingleRideHeader from "@/components/SingleRideHeader";
import { translationMixin } from '@/mixins/translationMixin';

import { INTERNAL_HOME_PATH } from '@/common/appHome';
export default {
  name: 'EditProfile',
  mixins: [translationMixin],
  components: {
    PageFooter,
    SingleRideHeader,
  },
  data() {
    return {
      isSaving: false,
    };
  },
  computed: {
    ...mapGetters([
      'isAppLoggedIn',
      'isUserManager',
    ]),
    ...mapGetters('editProfile', [
      'profileData',
      'isLoading',
      'uploadValue',
    ]),
    localName: {
      get() { return this.profileData.name || ''; },
      set(value) { this.updateField('name', value); }
    },
    localTitle: {
      get() { return this.profileData.title || ''; },
      set(value) { this.updateField('title', value); }
    },
    localAbout: {
      get() { return this.profileData.about || ''; },
      set(value) { this.updateField('about', value); }
    },
    localWebsiteLink: {
      get() { return this.profileData.website_link || ''; },
      set(value) { this.updateField('website_link', value); }
    },
    localFacebookLink: {
      get() { return this.profileData.facebook_link || ''; },
      set(value) { this.updateField('facebook_link', value); }
    },
    localInstagramLink: {
      get() { return this.profileData.instagram_link || ''; },
      set(value) { this.updateField('instagram_link', value); }
    },
    isUploading() {
      return this.uploadValue > 0 && this.uploadValue < 100;
    },
  },
  methods: {
    ...mapActions([
      'showModal',
    ]),
    ...mapActions('editProfile', [
      'fetchCurrentUserProfile',
      'updateUserProfile',
      'loadDefaultProfile',
    ]),
    updateField(field, value) {
      this.$store.commit('editProfile/setProfileField', { field, value });
    },
        
        
        
        
        // at this part we should lowed the user information with fetchCurrentUserProfile




        // Wait a bit for the store to update
    uploadImage(e) {
      let files = e.target.files || e.dataTransfer.files;
      if (!files || !files[0]) return;
      
      let imageData = files[0];
      var today = new Date();
      let myTime = today.toUTCString().split(', ')[1].split(' ').join('_');
      const userId = this.$store.getters.userId || 'profile';
      const storageRef = firebase.storage().ref(`profile_images/${userId}_${myTime}`).put(imageData);

      this.$store.commit('editProfile/setUploadValue', 0);

      storageRef.on(`state_changed`, snapshot => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        this.$store.commit('editProfile/setUploadValue', progress);
      }, error => {
        console.log(error.message);
        this.$store.commit('editProfile/setUploadValue', 0);
        alert(this.t('editProfileUploadError'));
      }, () => {
        this.$store.commit('editProfile/setUploadValue', 100);
        storageRef.snapshot.ref.getDownloadURL().then((url) => {
          this.updateField('profile_image', url);
          this.$store.commit('editProfile/setUploadValue', 0);
        });
      });
    },
    async saveProfile() {
      if (this.isSaving) return;
      
      // Validate required fields
      if (!this.profileData.name || this.profileData.name.trim() === '') {
        alert(this.t('editProfileEnterName'));
        return;
      }
      
      this.isSaving = true;
      
      try {
        const result = await this.updateUserProfile();
        if (result.success) {
          alert(result.message || this.t('editProfileSuccess'));
        } else {
          alert(result.message || this.t('editProfileUpdateError'));
        }
      } catch (error) {
        console.error('Error saving profile:', error);
        alert(this.t('editProfileUpdateErrorTryAgain'));
      } finally {
        this.isSaving = false;
      }
    },
    goBack() {
      this.$router.push('/user_rides');
    },
  },
  created() {
    if (!this.isAppLoggedIn) {
      this.showModal("pleaseLogInInOrderToEditRide");
      this.$router.push(INTERNAL_HOME_PATH);
      return;
    }
    
    if (!this.isUserManager) {
      this.$router.push(INTERNAL_HOME_PATH);
      return;
    }
    
    // Fetch current user profile information
    this.fetchCurrentUserProfile();
  },
  destroyed() {
    this.loadDefaultProfile();
  },
};
</script>

<style scoped>
.btn-greey {
  background: #6c757d!important;
  border-color: #6c757d!important;
  color: #fff!important;
}

.container {
  padding: 0 1.5rem ;
}

.row {
  flex-direction: row-reverse;
}
.row.row-ltr {
  flex-direction: row;
}
.profile-image-hint {
  margin: 4px 40px 12px 0;
  font-size: 14px;
  color: rgba(13, 47, 41, 0.7);
}
[dir="ltr"] .profile-image-hint {
  margin: 4px 0 12px 40px;
  text-align: left;
}
[dir="ltr"] .trip-contact-field label {
  text-align: left;
  margin-right: 0;
  margin-left: 40px;
}
[dir="ltr"] .trip-contact-input input,
[dir="ltr"] .trip-contact-input textarea {
  text-align: left;
}
[dir="ltr"] .trip-card-top.trip-card-top2 {
  text-align: left;
}
[dir="ltr"] a.trip-btn-bg {
  margin-left: 0;
  margin-right: 50px;
}
[dir="ltr"] .btn-spinner {
  margin-left: 0;
  margin-right: 8px;
}

.trip-card-top {
  text-align: center;
  margin-bottom: 50px;
}
a.trip-btn-bg {
  display: inline-block;
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
  text-align: center;
  color: #FFFFFF;
  background: #07A0C3;
  border-radius: 45px;
  padding: 9px 38px;
  margin-right: 50px;
}
a.trip-btn-bg.btn-greey {
  background: #6c757d!important;
  border-color: #6c757d!important;
  color: #fff!important;
}
a.trip-btn-outline {
  background: #FFFFFF;
  border: 1px solid #07A0C3;
  border-radius: 45px;
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
  text-align: center;
  color: rgba(13, 47, 41, 0.5);
  display: inline-block;
  width: 500px;
  padding: 6px 38px;
}
a.trip-btn-outline img{
  margin-left: 10px;
}

.trip-card-top.trip-card-top2{
  text-align: left;
  position: relative;
}
.trip-card-top h4 {
  font-weight: 600;
  font-size: 30px;
  line-height: 41px;
  color: #0D2F29;
  display: inline-block;
  position: absolute;
  top: 0;
  margin: 0;
  left: 50%;
  transform: translateX(-50%);
}
.trip-contact-wrap h4{
  display: none;
}
.trip-contact-field {
  margin-bottom: 25px;
}

.profile-image-section {
  margin-bottom: 35px;
}

.profile-image-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.profile-image-preview {
  position: relative;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  border: 4px solid #07A0C3;
  transition: all 0.3s ease;
}

.profile-image-preview:hover {
  box-shadow: 0 6px 20px rgba(7, 160, 195, 0.3);
  transform: scale(1.02);
}

.profile-preview-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.remove-image-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background: rgba(228, 36, 36, 0.9);
  color: white;
  border: none;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.remove-image-btn:hover {
  background: rgba(228, 36, 36, 1);
  transform: scale(1.1);
}

.profile-image-placeholder {
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 4px solid #e0e0e0;
  color: #999;
  transition: all 0.3s ease;
}

.profile-image-placeholder:hover {
  border-color: #07A0C3;
  background: linear-gradient(135deg, #e8f4f8 0%, #b8d4e0 100%);
}

.profile-image-placeholder i {
  margin-bottom: 10px;
  color: #07A0C3;
}

.profile-image-placeholder p {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
}
.trip-contact-field label{
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
  text-align: right;
  color: rgba(13, 47, 41, 0.5)!important;
  display: block;
  margin-bottom: 5px;
  margin-right: 40px;
}
.trip-contact-field label span{
  color: #E42424;
}

.trip-contact-input{
  position: relative;
}
.trip-contact-field img{
  width: 100%;
}
.trip-contact-input input,
.trip-contact-input textarea{
  height: 56px;
  width: 100%;
  background: #FFFFFF;
  border: 2px solid #07A0C3!important;
  border-radius: 45px;
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
  text-align: right;
  padding: 10px 20px!important;
  color: black !important;
  transition: all 0.3s ease;
}

.trip-contact-input input:focus,
.trip-contact-input textarea:focus {
  outline: none;
  border-color: #0590b0 !important;
  box-shadow: 0 0 0 3px rgba(7, 160, 195, 0.1);
  transform: translateY(-1px);
}

.trip-contact-input textarea{
  height: 300px;
  border-radius: 15px;
  resize: vertical;
}
.trip-input-icon {
  position: absolute;
  top: 13px;
  right: 25px;
}
.trip-contact-input p{
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
  text-align: center;
  margin:15px 0 0;
  color: rgba(228, 36, 36, 0.6)
}
.trip-contact-input input::-webkit-input-placeholder,
.trip-contact-input textarea::-webkit-input-placeholder{ /* WebKit, Blink, Edge */
  color: rgba(13, 47, 41, 0.5);
  opacity: 1;
}
.trip-contact-input input:-moz-placeholder,
.trip-contact-input textarea:-moz-placeholder{ /* Mozilla Firefox 4 to 18 */
  color: rgba(13, 47, 41, 0.5);
   opacity:  1;
}
.trip-contact-input input::-moz-placeholder,
.trip-contact-input textarea::-moz-placeholder{ /* Mozilla Firefox 19+ */
  color: rgba(13, 47, 41, 0.5);
   opacity:  1;
}
.trip-contact-input input:-ms-input-placeholder,
.trip-contact-input textarea:-ms-input-placeholder{ /* Internet Explorer 10-11 */
  color: rgba(13, 47, 41, 0.5);
   opacity: 1;
}
.trip-contact-input input::-ms-input-placeholder,
.trip-contact-input textarea::-ms-input-placeholder{ /* Microsoft Edge */
  color: rgba(13, 47, 41, 0.5);
   opacity: 1;
}

.trip-contact-input input::placeholder,
.trip-contact-input textarea::placeholder{ /* Most modern browsers support this now. */
  color: rgba(13, 47, 41, 0.5);
   opacity: 1;
}

.trip-submit-button{
  margin-top: 50px;
  text-align: center;
}

.trip-contact-form-section {
  padding-bottom: 150px;
}

a.trip-default-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
  text-align: center;
  color: #FFFFFF;
  background: #07A0C3;
  border-radius: 45px;
  padding: 12px 38px;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  min-width: 180px;
}

a.trip-default-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(7, 160, 195, 0.4);
}

a.trip-default-btn.btn-sky {
  background: #07A0C3;
}

a.trip-default-btn.btn-sky:hover {
  background: #0590b0;
}

a.trip-default-btn.btn-greey {
  background: #6c757d!important;
  border-color: #6c757d!important;
  color: #fff!important;
}

a.trip-default-btn.btn-greey:hover {
  background: #5a6268!important;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(108, 117, 125, 0.4);
}

a.trip-default-btn.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
  transform: none !important;
}

.btn-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #FFFFFF;
  border-radius: 50%;
  animation: btn-spin 0.6s linear infinite;
  margin-left: 8px;
}

@keyframes btn-spin {
  to { transform: rotate(360deg); }
}

.action-buttons-wrapper {
  margin-top: 40px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
}

.back-button-wrapper {
  text-align: center;
}

.info-message-wrapper {
  margin-top: 30px;
  margin-bottom: 30px;
}

.info-message {
  background: linear-gradient(135deg, #d1ecf1 0%, #bee5eb 100%);
  border: 1px solid #bee5eb;
  border-radius: 12px;
  padding: 20px;
  text-align: right;
  direction: rtl;
  display: flex;
  align-items: flex-start;
  gap: 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.info-message:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.info-icon {
  font-size: 24px;
  flex-shrink: 0;
  margin-top: 2px;
}

.info-content {
  flex: 1;
  color: #0c5460;
  font-size: 16px;
  line-height: 1.6;
}

.info-content strong {
  color: #0D2F29;
  font-weight: 700;
}

/* updloade img */
.btn_upload {
  cursor: pointer;
  display: inline-block;
  overflow: hidden;
  position: relative;
  padding: 5px 10px;
  height: 56px;
  background: #FFFFFF;
  border: 2px solid #07A0C3;
  border-radius: 45px;
  width: 100%;
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
  text-align: center;
  color: #07A0C3;
  padding: 15px 20px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn_upload:hover,
.btn_upload:focus {
  background-color: #07A0C3;
  color: #FFFFFF;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(7, 160, 195, 0.3);
}

.upload-progress {
  color: #07A0C3;
  font-weight: 700;
}

.upload-progress-bar {
  width: 100%;
  height: 4px;
  background: #e0e0e0;
  border-radius: 2px;
  margin-top: 10px;
  overflow: hidden;
}

.upload-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #07A0C3, #0AD6A1);
  transition: width 0.3s ease;
  border-radius: 2px;
}

.btn_upload input {
  cursor: pointer;
  height: 100%!important;
  position: absolute;
  filter: alpha(opacity=1);
  -moz-opacity: 0;
  opacity: 0;
  left: 0;
  top: 0;
  z-index: 99;
}

/* Removed old preview styles - using new profile-image-preview instead */

.btn-rmv1{
  display: none;
}

.rmv {
  cursor: pointer;
  color: #fff;
  border-radius: 30px;
  display: inline-block;
  position: absolute;
  right: 38%;
  top: 34px;
  z-index: 999;
  border: navajowhite;
  background-color: transparent;
  font-size: 32px;
}
img#ImgPreview {
  position: absolute;
  text-indent: 66em;
}

.trip-input-icon.trip-input-icon2 {
  position: absolute;
  top: 12px;
  right: 22px;
}

.spinner-border {
  display: inline-block;
  width: 2rem;
  height: 2rem;
  vertical-align: text-bottom;
  border: 0.25em solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spinner-border 0.75s linear infinite;
}

.spinner-border.text-primary {
  color: #07A0C3;
}

@keyframes spinner-border {
  to {
    transform: rotate(360deg);
  }
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

/* Responsive styles */
@media (max-width: 991px) {
  .trip-card-top h4{
    opacity: 0;
  }
  .trip-contact-wrap h4 {
    display: block;
    font-weight: 600;
    font-size: 14px;
    line-height: 19px;
    text-align: center;
    color: #0D2F29;
  }

  .trip-card-top.trip-card-top2{
    margin: 0;
  }
}

@media (max-width: 767px) {
  .trip-contact-field label {
    font-size: 12px;
  }
  .trip-contact-input input,
  .trip-contact-input textarea {
    height: 30px;
    font-size: 12px;
    padding: 10px 30px!important;
  }
  .trip-contact-input textarea{
    height: 94px;
    border-radius: 15px;
  }
  .trip-input-icon {
    top: 3px;
    right: 15px;
  }
  .trip-input-icon img{
    width: 12px;
  }

  .trip-contact-input p {
    font-size: 12px;
    margin: 6px 0 0;
  }
  .trip-submit-button {
    margin-top: 15px;
  }
  .trip-contact-form-section {
    padding-bottom: 30px;
  }

  .btn_upload {
    font-size: 14px;
    height: 45px;
    padding: 10px 20px;
  }

  .profile-image-preview,
  .profile-image-placeholder {
    width: 150px;
    height: 150px;
  }

  .remove-image-btn {
    width: 28px;
    height: 28px;
    font-size: 20px;
    top: 8px;
    right: 8px;
  }

  .profile-image-placeholder i {
    font-size: 2em;
  }

  .profile-image-placeholder p {
    font-size: 12px;
  }

  .info-message {
    padding: 15px;
    flex-direction: column;
    text-align: center;
  }

  .info-icon {
    font-size: 20px;
  }

  .info-content {
    font-size: 14px;
  }

  a.trip-default-btn {
    font-size: 16px;
    padding: 10px 30px;
    min-width: 160px;
  }

  .action-buttons-wrapper {
    margin-top: 30px;
    gap: 15px;
  }
}
</style>
